import classNames from 'classnames';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelXS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { messages } from '../messages';

const Content = styled(Vertical)`
  &.empty {
    filter: grayscale(1);
    opacity: 0.8;
  }
`;
const BrandBodyS = styled(BodyS)`
  font-family: var(--brand-font);
`;
const BrandLabelXS = styled(LabelXS)`
  font-family: var(--brand-font);
`;
const Footer = styled(Vertical).attrs({ center: true })`
  min-width: 90px;

  > ${Vertical} {
    width: 100%;
  }
`;

type Props = {
  border: boolean;
  percentBasisPoints: number | null;
  points: number | null;
  label: ReactNode;
  bar: ReactNode;
  cta?: ReactNode;
  Icon: FC<PropsWithChildren<{ children: ReactNode; border: boolean }>>;
};

export const BreakdownBlock = ({
  border,
  percentBasisPoints,
  points,
  label,
  bar,
  cta,
  Icon,
}: Props) => {
  return (
    <Vertical center gap={2}>
      <Content
        center
        className={classNames({ empty: !percentBasisPoints && !points })}
      >
        <Icon border={border}>
          {typeof percentBasisPoints === 'number' ? (
            <BrandBodyS bold>
              <Horizontal center gap={0}>
                <FormattedMessage
                  {...messages.numberPercent}
                  values={{
                    number: (
                      <FormattedNumber
                        value={percentBasisPoints / 100}
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    span: (...chunks: ReactNode[]) => (
                      <BrandBodyS bold color="var(--c-nd-600)">
                        {chunks}
                      </BrandBodyS>
                    ),
                  }}
                />
              </Horizontal>
            </BrandBodyS>
          ) : (
            <Vertical center gap={0}>
              <BrandBodyS bold className="text-center">
                <FormattedMessage
                  {...fantasy.ordinalPoints}
                  values={{
                    score: (
                      <FormattedNumber
                        value={points || 0}
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    sup: (...chunks: ReactNode[]) => (
                      <BrandLabelXS bold uppercase color="var(--c-nd-600)">
                        {chunks}
                      </BrandLabelXS>
                    ),
                  }}
                />
              </BrandBodyS>
            </Vertical>
          )}
        </Icon>
        <Footer>
          {label}
          {bar}
        </Footer>
      </Content>
      {cta}
    </Vertical>
  );
};
